import {
  addSavedViewCollaboratorRequestSchema,
  canonicalUuidSchema,
  createSavedViewRequestSchema,
  filterCatalogSchema,
  savedViewCollaboratorListSchema,
  savedViewCollaboratorSchema,
  savedViewListSchema,
  savedViewSchema,
  updateSavedViewCollaboratorRequestSchema,
  updateSavedViewRequestSchema,
  type AddSavedViewCollaboratorRequest,
  type CreateSavedViewRequest,
  type FilterCatalog,
  type SavedView,
  type SavedViewCollaborator,
  type UpdateSavedViewCollaboratorRequest,
  type UpdateSavedViewRequest,
} from "@meta-video/contracts";
import { z } from "zod";

import {
  ApiProtocolError,
  SessionExpiredError,
  fetchWithCsrfRetry,
  parseApiResponse,
  requestJson,
  runtimeFetch,
  type ApiRequestOptions,
} from "../../shared/api/client.js";

const deletedSchema = z.object({ deleted: z.literal(true) }).strict();
const savedViewErrorCodeSchema = z.enum([
  "SAVED_VIEW_CONFLICT",
  "SAVED_VIEW_DEPENDENCY_UNAVAILABLE",
  "SAVED_VIEW_INVITE_UNAVAILABLE",
  "SAVED_VIEW_NOT_FOUND",
  "SAVED_VIEW_REVISION_CONFLICT",
]);
const savedViewErrorSchema = z
  .object({
    code: savedViewErrorCodeSchema,
    current: savedViewSchema.optional(),
    message: z.string().min(1).max(1_000),
    statusCode: z.number().int().min(400).max(599),
  })
  .strict()
  .superRefine((error, context) => {
    if (
      (error.code === "SAVED_VIEW_REVISION_CONFLICT") !==
      (error.current !== undefined)
    ) {
      context.addIssue({
        code: "custom",
        message: "Only revision conflicts include the current saved view",
        path: ["current"],
      });
    }
    const expectedStatus = error.code === "SAVED_VIEW_NOT_FOUND"
      ? 404
      : error.code === "SAVED_VIEW_DEPENDENCY_UNAVAILABLE"
        ? 503
        : 409;
    if (error.statusCode !== expectedStatus) {
      context.addIssue({
        code: "custom",
        message: "Saved-view error code and status do not match",
        path: ["statusCode"],
      });
    }
  });

export type SavedViewApiErrorCode = z.output<typeof savedViewErrorCodeSchema>;

const SAFE_SAVED_VIEW_ERROR_MESSAGES: Readonly<Record<SavedViewApiErrorCode, string>> = {
  SAVED_VIEW_CONFLICT: "The saved-view change conflicts with current data.",
  SAVED_VIEW_DEPENDENCY_UNAVAILABLE: "Saved views are temporarily unavailable.",
  SAVED_VIEW_INVITE_UNAVAILABLE: "This person could not be added.",
  SAVED_VIEW_NOT_FOUND: "This saved view is no longer available.",
  SAVED_VIEW_REVISION_CONFLICT: "The saved view changed before this update.",
};

export class SavedViewApiError extends Error {
  constructor(
    readonly code: SavedViewApiErrorCode,
    readonly statusCode: number,
    readonly current: SavedView | null = null,
  ) {
    super(SAFE_SAVED_VIEW_ERROR_MESSAGES[code]);
    this.name = "SavedViewApiError";
  }
}

function fetcher(options: ApiRequestOptions): typeof fetch {
  return options.fetcher ?? runtimeFetch;
}

function uuid(value: string, label: string): string {
  return parseApiResponse(
    canonicalUuidSchema,
    value,
    `${label} has an invalid shape`,
  );
}

async function savedViewRequestJson(
  path: string,
  init: RequestInit,
  requestFetcher: typeof fetch,
  withCsrf = false,
): Promise<unknown> {
  const response = withCsrf
    ? await fetchWithCsrfRetry(
        path,
        init,
        requestFetcher,
      )
    : await requestFetcher(path, {
        ...init,
        credentials: "same-origin",
        headers: { accept: "application/json", ...init.headers },
      });
  if (response.status === 401) throw new SessionExpiredError();
  let value: unknown;
  try {
    value = await response.json();
  } catch {
    throw new ApiProtocolError(
      response.ok
        ? "Saved-view response was not valid JSON"
        : `Saved-view API request failed (${response.status})`,
    );
  }
  if (!response.ok) {
    const parsed = savedViewErrorSchema.safeParse(value);
    if (!parsed.success || parsed.data.statusCode !== response.status) {
      throw new ApiProtocolError(`Saved-view API request failed (${response.status})`);
    }
    throw new SavedViewApiError(
      parsed.data.code,
      parsed.data.statusCode,
      parsed.data.current ?? null,
    );
  }
  return value;
}

async function csrfRequest(
  path: string,
  method: "DELETE" | "PATCH" | "POST",
  body: unknown,
  options: ApiRequestOptions,
): Promise<unknown> {
  const requestFetcher = fetcher(options);
  return savedViewRequestJson(
    path,
    {
      body: JSON.stringify(body),
      headers: {
        "content-type": "application/json",
      },
      method,
      signal: options.signal,
    },
    requestFetcher,
    true,
  );
}

export async function getFilterCatalog(
  options: ApiRequestOptions = {},
): Promise<FilterCatalog> {
  const value = await requestJson(
    "/api/filters/catalog",
    { method: "GET", signal: options.signal },
    fetcher(options),
  );
  return parseApiResponse(filterCatalogSchema, value, "Filter catalog response has an invalid shape");
}

export async function getSavedViews(
  options: ApiRequestOptions = {},
): Promise<readonly SavedView[]> {
  const value = await savedViewRequestJson(
    "/api/saved-views",
    { method: "GET", signal: options.signal },
    fetcher(options),
  );
  return parseApiResponse(savedViewListSchema, value, "Saved-view list response has an invalid shape");
}

export async function getSavedView(
  viewId: string,
  options: ApiRequestOptions = {},
): Promise<SavedView> {
  const value = await savedViewRequestJson(
    `/api/saved-views/${uuid(viewId, "Saved-view id")}`,
    { method: "GET", signal: options.signal },
    fetcher(options),
  );
  return parseApiResponse(savedViewSchema, value, "Saved-view response has an invalid shape");
}

export async function createSavedView(
  input: CreateSavedViewRequest,
  options: ApiRequestOptions = {},
): Promise<SavedView> {
  const body = parseApiResponse(
    createSavedViewRequestSchema,
    input,
    "Saved-view create request has an invalid shape",
  );
  const value = await csrfRequest("/api/saved-views", "POST", body, options);
  return parseApiResponse(savedViewSchema, value, "Saved-view response has an invalid shape");
}

export async function updateSavedView(
  viewId: string,
  input: UpdateSavedViewRequest,
  options: ApiRequestOptions = {},
): Promise<SavedView> {
  const body = parseApiResponse(
    updateSavedViewRequestSchema,
    input,
    "Saved-view update request has an invalid shape",
  );
  const value = await csrfRequest(
    `/api/saved-views/${uuid(viewId, "Saved-view id")}`,
    "PATCH",
    body,
    options,
  );
  return parseApiResponse(savedViewSchema, value, "Saved-view response has an invalid shape");
}

export async function deleteSavedView(
  viewId: string,
  options: ApiRequestOptions = {},
): Promise<void> {
  const value = await csrfRequest(
    `/api/saved-views/${uuid(viewId, "Saved-view id")}`,
    "DELETE",
    {},
    options,
  );
  parseApiResponse(deletedSchema, value, "Saved-view delete response has an invalid shape");
}

export async function getSavedViewCollaborators(
  viewId: string,
  options: ApiRequestOptions = {},
): Promise<readonly SavedViewCollaborator[]> {
  const value = await savedViewRequestJson(
    `/api/saved-views/${uuid(viewId, "Saved-view id")}/collaborators`,
    { method: "GET", signal: options.signal },
    fetcher(options),
  );
  return parseApiResponse(
    savedViewCollaboratorListSchema,
    value,
    "Saved-view collaborator list response has an invalid shape",
  );
}

export async function addSavedViewCollaborator(
  viewId: string,
  input: AddSavedViewCollaboratorRequest,
  options: ApiRequestOptions = {},
): Promise<SavedViewCollaborator> {
  const body = parseApiResponse(
    addSavedViewCollaboratorRequestSchema,
    input,
    "Saved-view collaborator request has an invalid shape",
  );
  const value = await csrfRequest(
    `/api/saved-views/${uuid(viewId, "Saved-view id")}/collaborators`,
    "POST",
    body,
    options,
  );
  return parseApiResponse(
    savedViewCollaboratorSchema,
    value,
    "Saved-view collaborator response has an invalid shape",
  );
}

export async function updateSavedViewCollaborator(
  viewId: string,
  collaboratorUserId: string,
  input: UpdateSavedViewCollaboratorRequest,
  options: ApiRequestOptions = {},
): Promise<SavedViewCollaborator> {
  const body = parseApiResponse(
    updateSavedViewCollaboratorRequestSchema,
    input,
    "Saved-view collaborator update request has an invalid shape",
  );
  const value = await csrfRequest(
    `/api/saved-views/${uuid(viewId, "Saved-view id")}/collaborators/${uuid(
      collaboratorUserId,
      "Collaborator user id",
    )}`,
    "PATCH",
    body,
    options,
  );
  return parseApiResponse(
    savedViewCollaboratorSchema,
    value,
    "Saved-view collaborator response has an invalid shape",
  );
}

export async function removeSavedViewCollaborator(
  viewId: string,
  collaboratorUserId: string,
  options: ApiRequestOptions = {},
): Promise<void> {
  const value = await csrfRequest(
    `/api/saved-views/${uuid(viewId, "Saved-view id")}/collaborators/${uuid(
      collaboratorUserId,
      "Collaborator user id",
    )}`,
    "DELETE",
    {},
    options,
  );
  parseApiResponse(
    deletedSchema,
    value,
    "Saved-view collaborator delete response has an invalid shape",
  );
}
